import { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Tabs, Tab, Typography, Switch, FormControlLabel,
  Button, Alert, AlertTitle, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  getAccessAnalyzerConfig, updateAccessAnalyzerRegions, updateAccessAnalyzerUnusedAccess,
  disableAccessAnalyzer, startAccessAnalyzerRolesScan, getAccessAnalyzerRolesJob,
  fixAccessAnalyzerRoles,
} from '../../../services/accessAnalyzerApi';
import RolePermissionsPanel from '../../../components/portal/securityhub/RolePermissionsPanel';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import MultiSelectField from './MultiSelectField';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import {
  CK, FONT_BODY, listCardSx, sectionCardSx, sectionHeadingSx, insetPanelSx,
  dialogProps, dialogTitleSx,
} from '../portalStyles';

const ACCESS_ANALYZER_COLOR = '#AD1457';
const ACCESS_ANALYZER_COLOR_DARK = '#8C1049';

// AWS's valid range for UnusedAccessConfiguration.unusedAccessAge, and the console
// default. Kept in step with AccessAnalyzerSetup.js, which quotes the same numbers
// in the wizard.
const AGE_MIN = 1;
const AGE_MAX = 365;
const AGE_DEFAULT = 90;

// THE PAID SWITCH. The figure and its caveat are quoted here and in
// AccessAnalyzerSetup.js - the only two screens that can turn this family on - and
// the two must agree: a price that disagrees with itself between the wizard and
// settings is a support ticket at best.
const UNUSED_ACCESS_PRICE = '$0.20 per IAM role or user per month, per account';
const UNUSED_ACCESS_PRICE_NOTE = 'Charges start as soon as the analyzer is created and may not be prorated if you turn the feature off mid-month. AWS list price, US regions, as of writing - check the IAM Access Analyzer pricing page for your regions.';

const accessAnalyzerButtonSx = {
  backgroundColor: ACCESS_ANALYZER_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: ACCESS_ANALYZER_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };
const hintTextSx = { fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted };

const settingsHeader = (
  <PortalPageHeader
    icon={<SettingsIcon sx={{ fontSize: 24 }} />}
    title="Settings"
    subtitle="Manage unused-access analysis, analyzed regions, account permissions and Access Analyzer status"
    color={ACCESS_ANALYZER_COLOR}
  />
);

// AccessAnalyzerConfigResponse decodes linkingRegions to []string, but tolerate a
// raw JSON string so a DTO change cannot blank the form.
const toStringArray = (v) => {
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v || '[]'); } catch (_) { return []; }
};

const sameSet = (a, b) => a.length === b.length
  && [...a].sort().join('|') === [...b].sort().join('|');

// Region and unused-access updates claim the config atomically and are refused
// unless it is settled, so a Save dispatched during another operation is a
// guaranteed "another operation is in progress" error.
const configAllowsUpdate = (status) => status === 'enabled' || status === 'partial_failure';

export default function AccessAnalyzerSettingsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [unusedEnabled, setUnusedEnabled] = useState(false);
  const [unusedAge, setUnusedAge] = useState(String(AGE_DEFAULT));
  const [regions, setRegions] = useState([]);
  const [confirm, setConfirm] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [regionConfirmOpen, setRegionConfirmOpen] = useState(false);
  const [unusedOffConfirmOpen, setUnusedOffConfirmOpen] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // Extracted from the mount effect so the error state below can offer a real
  // retry. Seeding the form fields from the response is safe here and only here:
  // this runs when there is no config yet, so there is no edit to lose.
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const c = await getAccessAnalyzerConfig();
      const cfg = c?.data || null;
      setConfig(cfg);
      if (cfg) {
        setUnusedEnabled(!!cfg.unusedAccessEnabled);
        setUnusedAge(String(cfg.unusedAccessAge || AGE_DEFAULT));
        setRegions(toStringArray(cfg.linkingRegions));
      }
      setErr(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadConfig(); }, [loadConfig]);

  const operationRunning = config?.status === 'in_progress';

  // Every Save on this page is gated on a settled config, so without a poll a
  // visitor who arrives mid-operation would sit in front of permanently disabled
  // buttons until they reloaded by hand. Only the config is refreshed - never the
  // form fields, which would otherwise overwrite an edit in progress.
  useEffect(() => {
    if (!operationRunning) return undefined;
    const id = setInterval(async () => {
      try {
        const c = await getAccessAnalyzerConfig();
        setConfig(c?.data || null);
      } catch (_) { /* transient */ }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [operationRunning]);

  const save = async (fn, destination) => {
    setBusy(true);
    try {
      await fn();
      setErr(null);
      navigate(destination);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const closeDisableDialog = () => {
    setConfirmOpen(false);
    setConfirm('');
  };

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading Access Analyzer settings…" color={ACCESS_ANALYZER_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  // MUST precede the not-configured branch. GET /config rejecting leaves `config`
  // null, and the branch below would then tell an operator whose Access Analyzer is
  // configured and running that it was never set up - while discarding the actual
  // error, which is already sitting in `err`.
  if (err && !config) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={err} onRetry={loadConfig} />
        </motion.div>
      </motion.div>
    );
  }
  if (!config || config.status === 'disabled') {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert severity="info" action={
            <Button color="inherit" size="small" onClick={() => navigate('/securityhub/accessanalyzer')}>
              Go to setup
            </Button>
          }>
            IAM Access Analyzer is not set up yet for this customer. Complete the
            setup wizard first to manage unused access, regions, or disable the
            service.
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  const storedRegions = toStringArray(config.linkingRegions);
  const canUpdate = configAllowsUpdate(config.status);
  // "failed" is terminal, not transitional: no fan-out is running, and the update
  // gates refuse it until setup is re-run. Without this the Save buttons are dead
  // with no explanation whatsoever. Disable stays available - the backend accepts it
  // from every settled status - which is the other way out of this state.
  const setupFailed = config.status === 'failed';
  // The home region is force-prepended server-side and locked in the picker, so it
  // can never appear here; anything that does is genuinely being torn down.
  const removedRegions = storedRegions.filter((r) => !regions.includes(r));
  const addedRegions = regions.filter((r) => !storedRegions.includes(r));
  const regionsDirty = !sameSet(regions, storedRegions);

  const ageNumber = Number(unusedAge);
  const ageValid = Number.isInteger(ageNumber) && ageNumber >= AGE_MIN && ageNumber <= AGE_MAX;
  const unusedDirty = unusedEnabled !== !!config.unusedAccessEnabled
    // The age only counts as an edit while the family is ON: changing the number
    // with the switch off writes a window nothing reads, and offering Save for it
    // would dispatch a fan-out that changes nothing.
    || (unusedEnabled && ageValid && ageNumber !== config.unusedAccessAge);
  // Turning the family OFF deletes those analyzers and the findings they produced,
  // so it goes through a confirmation the way a region removal does. Turning it ON
  // only starts a charge that the alert above the switch already states.
  const turningUnusedOff = !unusedEnabled && !!config.unusedAccessEnabled;

  // Hand off to Enrollment: both saves fan out over every enrolled account, and that
  // page is where a per-account failure becomes actionable.
  const saveUnused = () => save(
    () => updateAccessAnalyzerUnusedAccess(unusedEnabled, ageValid ? ageNumber : AGE_DEFAULT),
    '/securityhub/accessanalyzer/enrollment',
  );

  const saveRegions = () => save(
    () => updateAccessAnalyzerRegions(regions),
    '/securityhub/accessanalyzer/enrollment',
  );

  // The dialog closes BEFORE the call, matching the region-confirm dialog. Left
  // until after the await, a rejection would sit behind the still-open modal on top
  // of the page-level error banner: the button re-enables, the user clicks again,
  // and nothing appears to happen.
  //
  // Destination is the Access Analyzer entry - it polls the config and shows the
  // teardown progress, then the setup wizard once the status reaches "disabled".
  const doDisable = () => {
    closeDisableDialog();
    return save(() => disableAccessAnalyzer(), '/securityhub/accessanalyzer');
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
      {err && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>
        </motion.div>
      )}

      {operationRunning && (
        <motion.div variants={sectionVariants}>
          <OperationProgress
            percent={config.progress}
            label={config.progressLabel || 'An Access Analyzer operation is running - settings are locked until it finishes'}
            sx={{ mb: 2 }}
          />
        </motion.div>
      )}

      {setupFailed && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={(
              <Button color="inherit" size="small" onClick={() => navigate('/securityhub/accessanalyzer')}>
                Re-run setup
              </Button>
            )}
          >
            IAM Access Analyzer setup failed{config.errorMessage ? `: ${config.errorMessage}` : '.'}
            {' '}
            Nothing is running and nothing will resume on its own. Unused access and
            regions cannot be changed until setup is re-run - disabling Access
            Analyzer from the Danger zone still works.
          </Alert>
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={listCardSx}>
        <Tabs
          value={tab}
          // Clear the banner on the way out: an error raised by a Save on one tab
          // describes that tab only, and left pinned it reads as a problem with
          // whichever tab the user has just moved to.
          onChange={(_, v) => { setErr(null); setTab(v); }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: `1px solid ${CK.border}`,
            '& .MuiTab-root': {
              fontFamily: FONT_BODY,
              fontWeight: 600,
              fontSize: '0.9rem',
              textTransform: 'none',
              minHeight: 56,
              color: CK.body,
              '&.Mui-selected': { color: ACCESS_ANALYZER_COLOR },
            },
            '& .MuiTabs-indicator': { backgroundColor: ACCESS_ANALYZER_COLOR },
          }}
        >
          <Tab label="Unused access" />
          <Tab label="Regions" />
          <Tab label="Permissions" />
          <Tab label="Danger zone" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Unused access</Typography>
            <Typography sx={{ ...bodyTextSx, mb: 2 }}>
              The second analyzer family: IAM roles, access keys, console passwords
              and permissions nobody has used inside the tracking window. External
              access analysis is free and always on; this one is not.
            </Typography>
            {/* Not fine print. This is the only switch in the capability that spends
                the customer's money, and the figure belongs next to it. */}
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
                Paid feature - {UNUSED_ACCESS_PRICE}
              </AlertTitle>
              Prism creates exactly <b>one unused-access analyzer per enrolled
              account, in the home region ({config.homeRegion}) only</b>. These
              analyzers are region-independent - IAM roles and users are global - so
              fanning them across your other regions would multiply the bill for
              byte-identical findings, and Prism deliberately does not do it.
              <br /><br />
              {UNUSED_ACCESS_PRICE_NOTE}
            </Alert>
            <Box sx={{ ...insetPanelSx, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={unusedEnabled}
                    onChange={(e) => setUnusedEnabled(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: ACCESS_ANALYZER_COLOR },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCESS_ANALYZER_COLOR },
                    }}
                  />
                }
                label={(
                  <Box>
                    <Typography
                      sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', fontWeight: 600, color: CK.navy }}
                    >
                      Analyze unused access
                    </Typography>
                    <Typography sx={bodyTextSx}>
                      Creates or tears down one ACCOUNT_UNUSED_ACCESS analyzer per
                      enrolled account in <b>{config.homeRegion}</b>.
                    </Typography>
                  </Box>
                )}
                sx={{ alignItems: 'flex-start', ml: 0, mr: 0 }}
              />
              <Box sx={{ mt: 2 }}>
                <TextField
                  label="Tracking period (days)"
                  type="number"
                  size="small"
                  value={unusedAge}
                  disabled={!unusedEnabled}
                  onChange={(e) => setUnusedAge(e.target.value)}
                  inputProps={{ min: AGE_MIN, max: AGE_MAX, step: 1 }}
                  error={unusedEnabled && !ageValid}
                  helperText={unusedEnabled && !ageValid
                    ? `Enter a whole number of days between ${AGE_MIN} and ${AGE_MAX}.`
                    : `${AGE_MIN}-${AGE_MAX} days. ${AGE_DEFAULT} is the AWS console default.`}
                  sx={{ maxWidth: 260 }}
                />
                <Typography sx={{ ...hintTextSx, mt: 1 }}>
                  AWS fixes the tracking window when an analyzer is created and
                  does not allow it to be changed afterwards. A new value here
                  applies to accounts enrolled from then on; accounts already
                  enrolled keep the window their analyzer was created with.
                  Changing an existing account&apos;s window means removing and
                  re-adding that account, which discards its unused-access
                  findings. A shorter window finds more; a longer one is safer to
                  act on.
                </Typography>
              </Box>
            </Box>
            {turningUnusedOff && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Saving deletes every unused-access analyzer Prism created and the
                unused-access findings cached from them. External-access findings are
                untouched.
              </Alert>
            )}
            <Button
              variant="contained"
              disabled={busy || !canUpdate || !unusedDirty || (unusedEnabled && !ageValid)}
              onClick={() => (turningUnusedOff ? setUnusedOffConfirmOpen(true) : saveUnused())}
              sx={accessAnalyzerButtonSx}
            >
              Save unused access
            </Button>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Analyzed regions</Typography>
            <Typography sx={{ ...bodyTextSx, mb: 1 }}>
              Home region: <b>{config.homeRegion}</b> (read-only)
            </Typography>
            <Typography sx={{ ...bodyTextSx, mb: 1 }}>
              This list is not a display filter. IAM Access Analyzer only evaluates
              policies on resources in the analyzer&apos;s own region, so adding a
              region creates an external-access analyzer there for every enrolled
              account; removing one deletes those analyzers and the findings Prism has
              cached from them. External-access analyzers are free, so regions buy
              coverage rather than cost.
            </Typography>
            <Typography sx={{ ...hintTextSx, mb: 1 }}>
              The unused-access analyzer is not affected by this list: it lives in the
              home region only, because IAM roles and users are global.
            </Typography>
            <Typography sx={{ ...hintTextSx, mb: 2 }}>
              IAM Access Analyzer is not available in every region. Selecting an
              unsupported region will cause enablement to fail for that region only -
              the rest of the update still succeeds.
            </Typography>
            <Box sx={{ mb: 2 }}>
              <MultiSelectField
                label="Analyzed regions"
                options={AWS_REGIONS.map((r) => ({ value: r.value, label: r.label }))}
                value={regions}
                onChange={setRegions}
                lockedValues={config.homeRegion ? [config.homeRegion] : []}
              />
            </Box>
            {removedRegions.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Saving will tear down <b>{removedRegions.join(', ')}</b>.
              </Alert>
            )}
            <Button
              variant="contained"
              disabled={busy || !canUpdate || !regionsDirty}
              onClick={() => (removedRegions.length > 0 ? setRegionConfirmOpen(true) : saveRegions())}
              sx={accessAnalyzerButtonSx}
            >
              Save regions
            </Button>
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <RolePermissionsPanel
              capabilityLabel="AWS IAM Access Analyzer"
              startScan={startAccessAnalyzerRolesScan}
              getJob={getAccessAnalyzerRolesJob}
              fix={fixAccessAnalyzerRoles}
            />
          </Box>
        )}

        {tab === 3 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Disable Access Analyzer</Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Disabling deletes the analyzers Prism created in every enrolled account
              and region and removes the cached findings. On a clean teardown Prism
              also detaches the IAM policy it added to each account&apos;s access
              role.
            </Alert>
            {/* The adoption asymmetry, stated where it matters most: an operator who
                disables the capability and then finds analyzers still running in AWS
                would otherwise read that as a failed teardown. */}
            <Alert severity="info" sx={{ mb: 2 }}>
              Analyzers that already existed when Prism enrolled an account were
              adopted, not created, and are <b>left running</b>
              {config.adoptedCount > 0 ? ` - ${config.adoptedCount} of them` : ''}.
              Deleting an analyzer destroys every finding it has ever produced, and
              those findings are yours, not Prism&apos;s.
            </Alert>
            <Button
              color="error"
              variant="contained"
              disabled={busy || operationRunning}
              onClick={() => setConfirmOpen(true)}
              sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
            >
              Disable Access Analyzer
            </Button>
          </Box>
        )}
      </Paper>
      </motion.div>

      <Dialog
        open={unusedOffConfirmOpen}
        onClose={() => setUnusedOffConfirmOpen(false)}
        {...dialogProps}
        maxWidth="xs"
      >
        <DialogTitle sx={dialogTitleSx}>Turn off unused access</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ ...bodyTextSx, mt: 1 }}>
            Prism deletes the unused-access analyzer it created in every enrolled
            account, and the unused-access findings cached from them are removed here.
            Unused IAM roles, access keys, console passwords and permissions stop
            being reported. External-access analysis and its findings are untouched.
          </Typography>
          <Typography sx={{ ...bodyTextSx, mt: 2 }}>
            Billing stops when the analyzers are deleted, but AWS may not prorate the
            month in progress. Turning the feature back on later starts a fresh
            analysis.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setUnusedOffConfirmOpen(false)}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: CK.body }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={() => { setUnusedOffConfirmOpen(false); saveUnused(); }}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
          >
            Turn off
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={regionConfirmOpen}
        onClose={() => setRegionConfirmOpen(false)}
        {...dialogProps}
        maxWidth="xs"
      >
        <DialogTitle sx={dialogTitleSx}>Confirm region removal</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ ...bodyTextSx, mt: 1 }}>
            Removing <b>{removedRegions.join(', ')}</b> deletes the external-access
            analyzer Prism created in
            {removedRegions.length === 1 ? ' that region' : ' those regions'} for every
            enrolled account, along with the findings cached from
            {removedRegions.length === 1 ? ' it' : ' them'}. Analyzers Prism adopted
            there are left running. Adding the
            {removedRegions.length === 1 ? ' region' : ' regions'} back later starts
            fresh analysis.
          </Typography>
          {addedRegions.length > 0 && (
            <Typography sx={{ ...bodyTextSx, mt: 2 }}>
              The same save enables <b>{addedRegions.join(', ')}</b>.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setRegionConfirmOpen(false)}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: CK.body }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={() => { setRegionConfirmOpen(false); saveRegions(); }}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
          >
            Save regions
          </Button>
        </DialogActions>
      </Dialog>

      {/* The typed customer ID is cleared on every exit. Left in state, reopening the
          dialog arrives with the type-to-confirm guard already satisfied and Disable
          live on the first click - which is the whole thing the guard exists to
          prevent. */}
      <Dialog
        open={confirmOpen}
        onClose={closeDisableDialog}
        {...dialogProps}
        maxWidth="xs"
      >
        <DialogTitle sx={dialogTitleSx}>Confirm disable</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ ...bodyTextSx, mt: 1 }}>
            Type the customer ID <b>{config.customerId}</b> to confirm:
          </Typography>
          <TextField
            fullWidth
            label="Customer ID"
            placeholder={config.customerId}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeDisableDialog}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: CK.body }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy || confirm !== config.customerId}
            onClick={doDisable}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
          >
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
